import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Briefcase,
  Bell,
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  Send,
  DollarSign,
  Calendar,
  MapPin,
  Building2,
  Loader2,
  Star,
  TrendingUp,
  Eye,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return `R${amount.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function ContractorPortal() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const contractorId = searchParams.get('id');
  
  const [loading, setLoading] = useState(true);
  const [contractor, setContractor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [activeTab, setActiveTab] = useState('jobs');
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [quoteForm, setQuoteForm] = useState({
    amount: '',
    description: '',
    estimated_days: '',
    valid_until: '',
  });

  useEffect(() => {
    if (contractorId) {
      fetchData();
    }
  }, [contractorId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch contractor profile (public endpoint)
      const profileRes = await fetch(`${API_URL}/api/contractors/profile/${contractorId}`);
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setContractor(profileData);
      }

      // Fetch notifications
      const notifRes = await fetch(`${API_URL}/api/contractors/notifications/${contractorId}`);
      if (notifRes.ok) {
        setNotifications(await notifRes.json());
      }

      // Fetch available jobs matching contractor's categories
      const jobsRes = await fetch(`${API_URL}/api/contractors/jobs/available?contractor_id=${contractorId}`);
      if (jobsRes.ok) {
        setJobs(await jobsRes.json());
      }

      // Fetch contractor's quotes
      const quotesRes = await fetch(`${API_URL}/api/contractors/quotes/by-contractor/${contractorId}`);
      if (quotesRes.ok) {
        setMyQuotes(await quotesRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Demo data
      setContractor({
        contractor_id: contractorId,
        company_name: 'Demo Contractor',
        contact_name: 'John Smith',
        email: 'demo@contractor.com',
        categories: ['plumber', 'general_maintenance'],
        status: 'approved',
        rating: 4.5,
        jobs_completed: 12,
      });
      setJobs([
        {
          job_id: 'job_001',
          title: 'Fix leaking pipe in unit 101',
          description: 'Water pipe under kitchen sink is leaking',
          category: 'plumber',
          unit_number: '101',
          location_details: 'Kitchen',
          status: 'open',
          created_at: new Date().toISOString(),
        },
        {
          job_id: 'job_002',
          title: 'Replace bathroom faucet',
          description: 'Main bathroom faucet is broken and needs replacement',
          category: 'plumber',
          unit_number: '205',
          status: 'open',
          created_at: new Date(Date.now() - 86400000).toISOString(),
        },
      ]);
      setNotifications([
        {
          notification_id: 'notif_001',
          title: 'New Job Available',
          message: 'A new plumber job has been posted',
          read: false,
          created_at: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitQuote = async () => {
    if (!quoteForm.amount || !quoteForm.description) {
      toast.error('Amount and description are required');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contractors/quotes/submit-portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_id: selectedJob.job_id,
          contractor_id: contractorId,
          amount: parseFloat(quoteForm.amount),
          description: quoteForm.description,
          estimated_days: parseInt(quoteForm.estimated_days) || null,
          valid_until: quoteForm.valid_until || null,
        })
      });

      if (res.ok) {
        toast.success('Quote submitted successfully!');
        setShowQuoteDialog(false);
        resetQuoteForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit quote');
      }
    } catch (error) {
      toast.success('Quote submitted! (Demo)');
      setShowQuoteDialog(false);
      resetQuoteForm();
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkNotificationRead = async (notifId) => {
    try {
      await fetch(`${API_URL}/api/contractors/notifications/${notifId}/read`, {
        method: 'PUT'
      });
      setNotifications(prev => prev.map(n => 
        n.notification_id === notifId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const resetQuoteForm = () => {
    setQuoteForm({
      amount: '',
      description: '',
      estimated_days: '',
      valid_until: '',
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      plumber: 'Plumber',
      electrician: 'Electrician',
      general_maintenance: 'General Maintenance',
      hvac: 'HVAC',
      landscaping: 'Landscaping',
      cleaning: 'Cleaning',
      security: 'Security',
      painter: 'Painter',
      carpenter: 'Carpenter',
      locksmith: 'Locksmith',
      pest_control: 'Pest Control',
    };
    return labels[category] || category;
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (!contractorId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Contractor ID Required</h2>
            <p className="text-slate-500 mb-4">
              Please access this portal using your unique contractor link.
            </p>
            <Input
              placeholder="Enter your Contractor ID"
              className="mb-4"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.value) {
                  navigate(`/contractor-portal?id=${e.target.value}`);
                }
              }}
            />
            <Button onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contractor-portal">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">{contractor?.company_name || 'Contractor Portal'}</h1>
                <p className="text-sm text-slate-500">BodyCorp365 Marketplace</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {contractor?.rating && (
                <div className="flex items-center gap-1 text-amber-500">
                  <Star className="w-4 h-4 fill-current" />
                  <span className="font-medium">{contractor.rating.toFixed(1)}</span>
                </div>
              )}
              <Badge className={contractor?.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                {contractor?.status === 'approved' ? 'Verified' : 'Pending Approval'}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{jobs.length}</p>
                  <p className="text-sm text-slate-500">Available Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{unreadCount}</p>
                  <p className="text-sm text-slate-500">New Notifications</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{contractor?.jobs_completed || 0}</p>
                  <p className="text-sm text-slate-500">Jobs Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{myQuotes.length}</p>
                  <p className="text-sm text-slate-500">Quotes Submitted</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Approval Warning */}
        {contractor?.status !== 'approved' && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Account Pending Approval</p>
                  <p className="text-sm text-amber-600">
                    Your contractor account is awaiting approval. You can view jobs but cannot submit quotes until approved.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="jobs">
              Available Jobs
              {jobs.length > 0 && <Badge className="ml-2 bg-blue-500">{jobs.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="quotes">My Quotes</TabsTrigger>
            <TabsTrigger value="notifications">
              Notifications
              {unreadCount > 0 && <Badge className="ml-2 bg-red-500">{unreadCount}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            {jobs.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">No Jobs Available</h3>
                  <p className="text-slate-500">Check back later for new job postings matching your services.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {jobs.map(job => (
                  <Card key={job.job_id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <Badge className="bg-indigo-100 text-indigo-700">
                          {getCategoryLabel(job.category)}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          {formatDate(job.created_at)}
                        </span>
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-2">{job.title}</h3>
                      <p className="text-sm text-slate-600 mb-4 line-clamp-2">{job.description}</p>
                      
                      <div className="space-y-2 text-sm text-slate-500 mb-4">
                        {job.unit_number && (
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            <span>Unit {job.unit_number}</span>
                          </div>
                        )}
                        {job.location_details && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span>{job.location_details}</span>
                          </div>
                        )}
                      </div>

                      <Button 
                        className="w-full bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => {
                          setSelectedJob(job);
                          setShowQuoteDialog(true);
                        }}
                        disabled={contractor?.status !== 'approved'}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Submit Quote
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            {myQuotes.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">No Quotes Submitted</h3>
                  <p className="text-slate-500">Submit quotes on available jobs to see them here.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myQuotes.map(quote => (
                  <Card key={quote.quote_id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{quote.job_title || 'Job Quote'}</h4>
                          <p className="text-sm text-slate-500">{quote.description?.substring(0, 100)}...</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-emerald-600">{formatCurrency(quote.amount)}</p>
                          <Badge variant={
                            quote.status === 'accepted' ? 'default' :
                            quote.status === 'rejected' ? 'destructive' : 'secondary'
                          }>
                            {quote.status}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            {notifications.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">No Notifications</h3>
                  <p className="text-slate-500">You'll receive notifications when new jobs are posted.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {notifications.map(notif => (
                  <Card 
                    key={notif.notification_id}
                    className={`cursor-pointer transition-all ${!notif.read ? 'border-l-4 border-l-indigo-500 bg-indigo-50/50' : ''}`}
                    onClick={() => !notif.read && handleMarkNotificationRead(notif.notification_id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-2 h-2 rounded-full mt-2 ${notif.read ? 'bg-slate-300' : 'bg-indigo-500'}`} />
                          <div>
                            <h4 className="font-medium text-slate-900">{notif.title}</h4>
                            <p className="text-sm text-slate-600">{notif.message}</p>
                            <p className="text-xs text-slate-400 mt-1">{formatDate(notif.created_at)}</p>
                          </div>
                        </div>
                        {notif.job_id && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              const job = jobs.find(j => j.job_id === notif.job_id);
                              if (job) {
                                setSelectedJob(job);
                                setShowQuoteDialog(true);
                              }
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Job
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Submit Quote Dialog */}
        <Dialog open={showQuoteDialog} onOpenChange={setShowQuoteDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Submit Quote</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedJob && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Badge className="mb-2">{getCategoryLabel(selectedJob.category)}</Badge>
                  <h4 className="font-medium">{selectedJob.title}</h4>
                  <p className="text-sm text-slate-600 mt-1">{selectedJob.description}</p>
                  {selectedJob.unit_number && (
                    <p className="text-sm text-slate-500 mt-2">Unit {selectedJob.unit_number}</p>
                  )}
                </div>
              )}
              
              <div>
                <Label>Quote Amount (R) *</Label>
                <Input
                  type="number"
                  value={quoteForm.amount}
                  onChange={(e) => setQuoteForm({...quoteForm, amount: e.target.value})}
                  placeholder="e.g., 2500.00"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Description / Scope of Work *</Label>
                <Textarea
                  value={quoteForm.description}
                  onChange={(e) => setQuoteForm({...quoteForm, description: e.target.value})}
                  placeholder="Describe what's included in your quote, materials, labor, etc."
                  rows={4}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Estimated Days</Label>
                  <Input
                    type="number"
                    value={quoteForm.estimated_days}
                    onChange={(e) => setQuoteForm({...quoteForm, estimated_days: e.target.value})}
                    placeholder="e.g., 2"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Valid Until</Label>
                  <Input
                    type="date"
                    value={quoteForm.valid_until}
                    onChange={(e) => setQuoteForm({...quoteForm, valid_until: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowQuoteDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleSubmitQuote} 
                disabled={submitting || !quoteForm.amount || !quoteForm.description}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Submit Quote
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
